import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, CheckIcon, MessageSquareIcon, RotateCcwIcon, TruckIcon, CreditCardIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { PURCHASE_STATUS, isCurrent, type PurchaseOrder } from '../../data/myOrders';
import { money } from '../../data/food';
import { formatWhen } from '../../utils/orderDates';
interface MyOrderDetailProps {
  order: PurchaseOrder | null;
  onClose: () => void;
}

/** One order in full — kept as a slide-over so the list keeps its place. */
export function MyOrderDetail({
  order,
  onClose
}: MyOrderDetailProps) {
  const {
    add,
    cancelOrder
  } = useCart();
  const navigate = useNavigate();
  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => {
    setConfirmCancel(false);
    if (!order) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [order, onClose]);
  const buyAgain = (o: PurchaseOrder) => o.lines.forEach(l => add({
    ...l,
    merchant: o.merchant
  }, true));
  return createPortal(<AnimatePresence data-id="element-2943" data-mp="ugdo5cflak7q">
      {order && <div className="fixed inset-0 z-[85] flex justify-end" data-id="element-2944" data-mp="ujjcgpge9i2e">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-2945" data-mp="pjn98sfitunw" />
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} aria-label={`Order ${order.id}`} className="relative w-full sm:w-[440px] h-full bg-white shadow-large flex flex-col" data-id="element-2946" data-mp="bs8n4cqbhdcm">
            <header className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-slate-100" data-id="element-2947" data-mp="3t939yghisl31">
              <div className="min-w-0" data-id="element-2948" data-mp="3fadawvb54bc1">
                <p className="text-[12px] text-slate-500" data-id="element-2949" data-mp="3fbekevcicprn">
                  {order.id} · {formatWhen(order.placedAt)}
                </p>
                <h2 className="font-display font-bold text-lg text-dark-navy mt-0.5 truncate" data-id="element-2950" data-mp="avna92yuwvw7">
                  {order.merchant}
                </h2>
                <span className={`inline-flex mt-2 px-2.5 py-1 rounded-full text-[12px] font-semibold ${PURCHASE_STATUS[order.status].tone}`} data-id="element-2951" data-mp="ftykvfwz7afh">
                  {PURCHASE_STATUS[order.status].label}
                </span>
              </div>
              <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2952" data-mp="tt6kg9x1wkk7">
                <XIcon className="w-4 h-4" data-id="element-2953" data-mp="2eb8qc9iih3a6" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7" data-id="element-2954" data-mp="2t1fynwet15x5">
              <Timeline order={order} data-id="element-2955" data-mp="2o23a978abqsg" />

              <section data-id="element-2956" data-mp="2o34rjtfw99im">
                <h3 className="text-sm font-bold text-dark-navy mb-3" data-id="element-2957" data-mp="2o2v7snlfzyld">
                  Items
                </h3>
                <ul className="space-y-3" data-id="element-2958" data-mp="20ec1gdlydvvx">
                  {order.lines.map(l => <li key={l.listingId + l.summary} className="flex gap-3" data-id="element-2959" data-mp="1vfimshuxjyu6">
                      <img src={l.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100 flex-shrink-0" data-id="element-2960" data-mp="2ac9owyvznonh" />
                      <div className="flex-1 min-w-0" data-id="element-2961" data-mp="3lrybhp5bylnr">
                        <p className="text-sm font-semibold text-dark-navy leading-snug" data-id="element-2962" data-mp="3grk0by8fmvli">
                          {l.title}
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-2963" data-mp="1qbzwtv5f1w8r">
                          {l.summary}
                          {l.qty > 1 ? ` · ×${l.qty}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-dark-navy" data-id="element-2964" data-mp="249xxxyuq7w6b">
                        {money(l.unitPrice * l.qty)}
                      </span>
                    </li>)}
                </ul>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100" data-id="element-2965" data-mp="2e6kisnn1yn8c">
                  <span className="text-sm text-slate-500" data-id="element-2966" data-mp="2izlzn16tkwuk">Total</span>
                  <span className="font-display font-bold text-lg text-dark-navy" data-id="element-2967" data-mp="2n0fobws36foi">
                    {money(order.total)}
                  </span>
                </div>
              </section>

              <section className="space-y-3" data-id="element-2968" data-mp="2wxu19meswmc2">
                <Fact icon={TruckIcon} text={order.fulfilment} data-id="element-2969" data-mp="2wylmc3arv1o3" />
                <Fact icon={CreditCardIcon} text={order.payment} data-id="element-2970" data-mp="372aiaj40o9hg" />
              </section>

              {order.status === 'cancelled' && order.cancelReason && <p className="text-[13px] text-slate-500" data-id="element-2971" data-mp="325abg5zcf2pa">{order.cancelReason}</p>}
            </div>

            <footer className="px-6 py-4 border-t border-slate-100 space-y-2.5" data-id="element-2972" data-mp="2t2iwhw79dosw">
              {confirmCancel ? <div className="space-y-2.5" data-id="element-2973" data-mp="3psrwzjs0l4jt">
                  <p className="text-[13px] text-slate-600" data-id="element-2974" data-mp="3prqo9lyh0u59">
                    Cancel this order? {order.merchant} will be told straight
                    away and any payment refunded.
                  </p>
                  <div className="flex gap-2" data-id="element-2975" data-mp="3g0cduj4ze9xc">
                    <button onClick={() => setConfirmCancel(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-2976" data-mp="3c02a7d4nvvln">
                      Keep order
                    </button>
                    <button onClick={() => {
                cancelOrder(order.id);
                setConfirmCancel(false);
              }} className="flex-1 py-2.5 rounded-xl bg-error text-white text-sm font-bold hover:opacity-90 transition-opacity duration-150 ease-out" data-id="element-2977" data-mp="21x3m4ga3ljan">
                      Cancel order
                    </button>
                  </div>
                </div> : <>
                  <div className="flex gap-2" data-id="element-2978" data-mp="1963hc33y0bhx">
                    <button onClick={() => {
                onClose();
                navigate('/messages');
              }} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-2979" data-mp="197de8oz27dmz">
                      <MessageSquareIcon className="w-4 h-4" data-id="element-2980" data-mp="ww3zgnrprtty" />
                      Message seller
                    </button>
                    {!isCurrent(order) && <button onClick={() => buyAgain(order)} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2981" data-mp="16mfh1d4bm1jb">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-2982" data-mp="1bkh0w09km4vu" />
                        Buy again
                      </button>}
                  </div>
                  {order.status === 'received' && <button onClick={() => setConfirmCancel(true)} className="w-full text-center text-[13px] font-semibold text-slate-400 hover:text-error transition-colors duration-150 ease-out py-1" data-id="element-2983" data-mp="3rbv2dskrsy1k">
                      Cancel order
                    </button>}
                </>}
            </footer>
          </motion.aside>
        </div>}
    </AnimatePresence>, document.body);
}
function Fact({
  icon: Icon,
  text
}: {
  icon: React.ElementType;
  text: string;
}) {
  return <p className="flex items-start gap-3 text-sm text-slate-600" data-id="element-2984" data-mp="3fad02scw1aqx">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-2985" data-mp="2hmbc48kynycl" />
      {text}
    </p>;
}

/** Received → In progress → Completed, with the times each was reached. */
function Timeline({
  order
}: {
  order: PurchaseOrder;
}) {
  const cancelled = order.status === 'cancelled';
  const steps = cancelled ? [{
    label: 'Order received',
    at: order.placedAt,
    done: true
  }, {
    label: 'Cancelled',
    at: order.cancelledAt,
    done: true
  }] : [{
    label: 'Order received',
    at: order.placedAt,
    done: true
  }, {
    label: 'In progress',
    at: order.progressAt,
    done: order.status === 'progress' || order.status === 'completed'
  }, {
    label: 'Completed',
    at: order.completedAt,
    done: order.status === 'completed'
  }];
  return <ol className="space-y-0" data-id="element-2986" data-mp="1hl7u8hj3m4ci">
      {steps.map((s, i) => <li key={s.label} className="flex gap-3" data-id="element-2987" data-mp="1hkocp1xd1hz0" data-mpi={i}>
          <div className="flex flex-col items-center" data-id="element-2988" data-mp="1cmd3mtew3bhu">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center ${s.done ? cancelled && i === steps.length - 1 ? 'bg-slate-400 text-white' : 'bg-primary-indigo text-white' : 'border-2 border-slate-200 bg-white'}`} data-id="element-2989" data-mp="1cjqwguvwuy05">
              {s.done && <CheckIcon className="w-3 h-3" data-id="element-2990" data-mp="2xyoao7cnaxjm" />}
            </span>
            {i < steps.length - 1 && <span className={`w-px flex-1 min-h-[20px] ${steps[i + 1].done ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-2991" data-mp="2t0wg15km8xbq" />}
          </div>
          <div className="pb-4" data-id="element-2992" data-mp="15rcds9at4fdu">
            <p className={`text-sm leading-5 ${s.done ? 'font-semibold text-dark-navy' : 'text-slate-400'}`} data-id="element-2993" data-mp="15ui2hnfixf91">
              {s.label}
            </p>
            {s.done && s.at && <p className="text-[12px] text-slate-500" data-id="element-2994" data-mp="s0qrsbq23pbq">{formatWhen(s.at)}</p>}
          </div>
        </li>)}
    </ol>;
}